/* 
  Price Lookup 
  Looks up the price of each product thumbnail based on input width and height
  using the pricing matrix metafields.
*/

let productThumbs = document.querySelectorAll('[data-product-thumbnail]');
const priceLookupContainers = document.querySelectorAll('[data-price-lookup]');
const updatePricing = 'Update Pricing';
const pricingUpdated = "Updated!";
const pricingErrorMessage = "Measurements exceed product maximum.";

priceLookupContainers.forEach(container => {
  const widthInput = container?.querySelector<HTMLInputElement>('[data-price-lookup-width]');
  const heightInput = container?.querySelector<HTMLInputElement>('[data-price-lookup-height]');
  const submitButton = container?.querySelector<HTMLButtonElement>('[data-price-lookup-submit]');
  const clearButton = container?.querySelector<HTMLButtonElement>('[data-price-lookup-clear]');
  const widthLabel = container?.querySelector<HTMLLabelElement>('[data-width-label]');
  const heightLabel = container?.querySelector<HTMLLabelElement>('[data-height-label]');
  const productsContainer = document.querySelector<HTMLElement>('[data-collection-template-products]');
  const widthErrors = container?.querySelectorAll<HTMLElement>('[data-error-width]');
  const heightErrors = container?.querySelectorAll<HTMLElement>('[data-error-height]');
  
  const widthCookie = getCookie('inputWidth');
  const heightCookie = getCookie('inputHeight');

  if (widthInput && widthCookie) widthInput.value = widthCookie;
  if (heightInput && heightCookie) heightInput.value = heightCookie;

  [widthInput, heightInput].forEach(input => {
    input?.addEventListener('wheel', preventDefault, { passive: false });
  });

  function preventDefault(e: WheelEvent) {
    e.preventDefault();
  }

  function calcPriceFromDimensionForProduct(productPricing: any, width: number, drop: number): number {

    const pricingToUse = productPricing.pricing || productPricing;
    if (!pricingToUse) return 0;

    const dropTiers = Object.keys(pricingToUse).map(Number).sort((a, b) => a - b);
    const activeDropTier = dropTiers.find(tier => tier >= width) || dropTiers[dropTiers.length - 1];
    if (!activeDropTier) return 0;

    const widthPricing = pricingToUse[activeDropTier];
    const widthTiers = Object.keys(widthPricing).map(Number).sort((a, b) => a - b);
    const activeWidthTier = widthTiers.find(tier => tier >= drop) || widthTiers[widthTiers.length - 1];
    if (!activeWidthTier) return 0;

    const priceForDimension = parseFloat(widthPricing[activeWidthTier]);
    return isNaN(priceForDimension) ? 0 : priceForDimension;
  }

  function modifyPrice(price: number, discount: string): number {
    const discountValue = parseInt(discount ?? '0');
    if (discountValue <= 0) return price;
    const discountIsPercentage = discount.includes('%');
    return discountIsPercentage ? price * (1 - discountValue / 100) : price - discountValue;
  }

  function getPrice(price: number): string {
    let formattedPrice = price.toFixed(2);
    return formattedPrice.endsWith('.00') ? price.toFixed(0) : formattedPrice;
  }

  function extractDimensionsFromPricing(pricing: any): { widths: number[], drops: number[] } {
    const drops = Object.keys(pricing).map(Number).sort((a, b) => a - b);
    const widthPricing = drops.length > 0 ? pricing[drops[drops.length - 1]] : {};
    const widths = Object.keys(widthPricing).map(Number).sort((a, b) => a - b);

    return { widths: widths.length > 0 ? widths : [], drops: drops.length > 0 ? drops : [] };
  }

  function getAllDimensions(): { min_width: number, max_width: number, min_drop: number, max_drop: number } {
    productThumbs = document.querySelectorAll('[data-product-thumbnail]');
    let min_width = Infinity, max_width = -Infinity;
    let min_drop = Infinity, max_drop = -Infinity;

    productThumbs.forEach(thumb => {
      const schemaElement = thumb.querySelector('script[data-product-thumbnail-schema]');
      if (schemaElement) {
        const schemaData = JSON.parse(schemaElement.textContent || '{}');
        const pricing = schemaData.pricing || schemaData;
        if (pricing) {
          const { widths, drops } = extractDimensionsFromPricing(pricing);
          if (widths.length > 0) {
            min_width = Math.min(min_width, ...drops);
            max_width = Math.max(max_width, ...drops);
          }
          if (drops.length > 0) {
            min_drop = Math.min(min_drop, ...widths);
            max_drop = Math.max(max_drop, ...widths);
          }
        }
      }
    });

    return {
      min_width: min_width === Infinity ? 0 : min_width,
      max_width: max_width === -Infinity ? 1000 : max_width,
      min_drop: min_drop === Infinity ? 0 : min_drop,
      max_drop: max_drop === -Infinity ? 1000 : max_drop
    };
  }

  function validateInputs(): boolean {
    const dimensions = getAllDimensions();
    const widthValue = parseFloat(widthInput?.value || '0');
    const dropValue = parseFloat(heightInput?.value || '0');
    let isValid = true;

    if (widthValue < dimensions.min_width || widthValue > dimensions.max_width || widthValue <= 0) {
      widthLabel?.classList.add('has-error');
      widthErrors.forEach(err => {
        if (err) err.innerHTML = `Width must be within ${dimensions.min_width} - ${dimensions.max_width}.`;
      })
      isValid = false;
    } else {
      widthLabel?.classList.remove('has-error');
      widthErrors.forEach(err => {
        if (err) err.innerHTML = '';
      })
      setCookie('inputWidth', String(widthValue), 30);
    }

    if (dropValue < dimensions.min_drop || dropValue > dimensions.max_drop || dropValue <= 0) {
      heightLabel?.classList.add('has-error');
      heightErrors.forEach(err => {
        if (err) err.innerHTML = `Height must be within ${dimensions.min_drop} - ${dimensions.max_drop}.`;
      })
      isValid = false;
    } else {
      
      heightLabel?.classList.remove('has-error');
      heightErrors.forEach(err => {
        if (err) err.innerHTML = '';
      })
      setCookie('inputHeight', String(dropValue), 30);  
    }

    return isValid;
  }

  function calculatePrice(thumb: Element) {
    const schemaElement = thumb.querySelector('script[data-product-thumbnail-schema]');
    if (!schemaElement) return;

    const schemaData = JSON.parse(schemaElement.textContent || '{}');
    const productPricing = schemaData.pricing || schemaData;
    const money = thumb.querySelector('[data-product-money]');

    if (!productPricing || !money) return;

    const width = parseFloat(widthInput?.value || '0');
    const drop = parseFloat(heightInput?.value || '0');  

    if ((width > schemaData.max_width || width < schemaData.min_width) || (drop > schemaData.max_drop || drop < schemaData.min_drop)) {  
      money.innerHTML = pricingErrorMessage;
      money.classList.add('has-error');
      return;
    }

    let priceEstimate = calcPriceFromDimensionForProduct(productPricing, width, drop);  

    if (schemaData.enable_sale_prices) {
      priceEstimate = modifyPrice(priceEstimate, schemaData.discount);
    }

    money.innerHTML = `From $${getPrice(priceEstimate)}`;
    money.classList.remove('has-error');
  }

  function setCookie(name: string, value: string | boolean, days?: number) {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  }

  function getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for(let i=0; i < ca.length; i++) {
      let c = ca[i].trim();
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  submitButton?.addEventListener('click', (event) => {
    event.preventDefault();
    productThumbs = document.querySelectorAll('[data-product-thumbnail]');

    if (validateInputs()) {
      container?.classList.add('has-price-lookup');
      submitButton.innerHTML = pricingUpdated;

      productThumbs.forEach(thumb => {
        calculatePrice(thumb);
      });

      setTimeout(() => {
        submitButton.innerHTML = updatePricing;
        productsContainer?.scrollIntoView({ behavior: 'smooth' });
      }, 1000);
    }
  });

  [widthInput, heightInput].forEach(input => {
    input?.addEventListener('keyup', (event) => {
      if (validateInputs() && event.key === 'Enter') {
        submitButton?.click();
      }
    });
  });

  clearButton?.addEventListener('click', () => {
    productThumbs = document.querySelectorAll('[data-product-thumbnail]');
    container?.classList.remove('has-price-lookup');
    
    if (submitButton) submitButton.innerHTML = updatePricing;

    [widthInput, heightInput].forEach(input => {
      if(input) input.value = '1000'
    });
    productThumbs.forEach(thumb => {
      calculatePrice(thumb);
    });
    [widthInput, heightInput].forEach(input => {
      if (input) input.value = '';
    });
    setCookie('inputWidth', false, 0);
    setCookie('inputHeight', false, 0);
    widthLabel?.classList.remove('has-error');
    heightLabel?.classList.remove('has-error');
  });


  window.addEventListener('thumbnails_rendered', function () {
    setTimeout(() => {
      productThumbs = document.querySelectorAll('[data-product-thumbnail]');
      if (container?.classList.contains('has-price-lookup') && submitButton) {
        productThumbs.forEach(thumb => {
          calculatePrice(thumb);
        });
      }
    }, 1000);
  });
});
